import React from "react";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import {
    INTEGER_MAX_VALUE_OPTIONS,
    IntegerQuestionOptions,
    RationalKindOptions,
} from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { showsIntegerMaxValue, showsRationalKind } from "./AdvancedSettings.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The integer dials (ADR 0030 §3) and, for the three rational question types,
 * decimals-fractions-or-both (ADR 0122 §2).
 *
 * The operator bar is hidden for this format, so unlike algebra the question
 * type is the only thing naming the operations - `Adding & Subtracting` beside
 * a ticked multiplication button would be two controls disagreeing.
 *
 * The kind control is basic tier, and appears only for the rational types; the
 * largest-number dial is hidden when those are set to fractions, which ignore
 * it. Both conditions are `AdvancedSettings.ts`'s, not restated here. Reads its
 * own slice of the settings (ADR 0081), so a change elsewhere on the panel does
 * not redraw it.
 */
export const IntegerSettings = React.memo(function IntegerSettings() {
    const { getSettings, setSettings } = useSettingsActions();
    const { question, maxValue, kind, language, showsKind, showsMax } = useSetting((settings) => ({
        question: settings.IntegerQuestion,
        maxValue: settings.IntegerMaxValue,
        kind: settings.RationalKind,
        language: settings.Language,
        showsKind: showsRationalKind(settings),
        showsMax: showsIntegerMaxValue(settings),
    }));
    const t = useTranslation();

    function handleQuestionChange(value: IntegerQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...getSettings(), IntegerQuestion: value });
    }

    function handleKindChange(value: RationalKindOptions) {
        if (!value) {
            return;
        }
        setSettings({ ...getSettings(), RationalKind: value });
    }

    return (
        <div className="integerSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={question}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(IntegerQuestionOptions), language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            {showsKind && (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{t('settings.rationalKindLabel')}</span>
                    </div>
                    <div className="regroupingContainer">
                        <SelectButton
                            value={kind}
                            onChange={(e) => handleKindChange(e.value)}
                            options={withLabels(Object.values(RationalKindOptions), language)}
                            className="select regrouping"
                            aria-label={t('settings.rationalKindAria')}
                        />
                    </div>
                </>
            )}
            {showsMax && (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{t('settings.largestNumberLabel')}</span>
                    </div>
                    <div className="radioButtons" role="radiogroup" aria-label={t('settings.largestNumberAria')}>
                        {INTEGER_MAX_VALUE_OPTIONS.map((option) => (
                            <div key={option}>
                                <RadioButton
                                    inputId={`integerMaxValue-${option}`}
                                    value={option}
                                    checked={maxValue === option}
                                    onChange={() => setSettings({ ...getSettings(), IntegerMaxValue: option })}
                                />
                                <label htmlFor={`integerMaxValue-${option}`}>{option}</label>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
});

export default IntegerSettings;
